import { faceNodeGetFace } from "building-generation/buildingGraph/faceNodeParent";
import {
  faceNodeGetParentHandle,
  volumeNodeParentHandleGetRoomId,
} from "building-generation/buildingGraphFilter/entityFilterSelectors";
import type { BuildingGraph, BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import { aabb2ByPolyLine2s, aabb2Grow } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { transformFaceLoopBasis } from "geom/brep";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { planarGraphHalfEdgeStripToPolyLine2 } from "geom/planarGraph";
import { polyLine2Copy } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

const NONDEGENERATE_FACE_LOOP_LENGTH = 6;

function faceNodeContributesToRoomBackedExtents(
  faceNodeId: string,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): boolean {
  const faceNode = buildingGraph.faceNodes?.[faceNodeId];
  if (!faceNode) {
    return false;
  }

  const parentHandle = faceNodeGetParentHandle(faceNode, buildingGraph);
  if (!volumeNodeParentHandleGetRoomId(parentHandle)) {
    return false;
  }

  if (planLevelIds !== undefined) {
    const levelId = parentHandle?.type === "ROOM" ? parentHandle.levelId : undefined;
    if (levelId === undefined || !planLevelIds.has(levelId)) {
      return false;
    }
  }

  return true;
}

function faceHasRoomBackedTopologyContributor(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  faceIndex: number,
  planLevelIds?: ReadonlySet<string>,
): boolean {
  const topologyFaceNodeSet = new Set(topology.faceNodeIds);
  const nodeIds = topology.planarGraphFaceToFaceNodeIds[faceIndex] ?? [];
  for (const faceNodeId of nodeIds) {
    if (!topologyFaceNodeSet.has(faceNodeId)) {
      continue;
    }
    if (faceNodeContributesToRoomBackedExtents(faceNodeId, buildingGraph, planLevelIds)) {
      return true;
    }
  }
  return false;
}

function faceNodeRoomBackedPlaneUVFaceLoopsForNode(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  faceNodeId: string,
  planLevelIds?: ReadonlySet<string>,
): PolyLine2[] {
  const loops: PolyLine2[] = [];
  const refPlane = topology.plane;
  const faceNode = buildingGraph.faceNodes?.[faceNodeId];
  if (!faceNode || !faceNodeContributesToRoomBackedExtents(faceNodeId, buildingGraph, planLevelIds)) {
    return loops;
  }

  const faceNodeFace = faceNodeGetFace(faceNode, buildingGraph);
  if (!faceNodeFace) {
    return loops;
  }

  for (const loop of faceNodeFace.loops) {
    if (loop.length < NONDEGENERATE_FACE_LOOP_LENGTH) {
      continue;
    }
    loops.push(transformFaceLoopBasis(polyLine2Copy(loop), faceNodeFace.plane, refPlane));
  }

  return loops;
}

function topologyFaceNodeRoomBackedPlaneUVFaceLoops(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): PolyLine2[] {
  const loops: PolyLine2[] = [];

  for (const faceNodeId of topology.faceNodeIds) {
    if (!faceNodeContributesToRoomBackedExtents(faceNodeId, buildingGraph, planLevelIds)) {
      continue;
    }
    loops.push(...faceNodeRoomBackedPlaneUVFaceLoopsForNode(topology, buildingGraph, faceNodeId, planLevelIds));
  }

  return loops;
}

/** U bounds of a single room-backed face node projected into topology plane UV. */
export function faceNodeRoomBackedTopologyPlanUBounds(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  faceNodeId: string,
  planLevelIds?: ReadonlySet<string>,
): TopologyPlanUBounds | undefined {
  const loops = faceNodeRoomBackedPlaneUVFaceLoopsForNode(topology, buildingGraph, faceNodeId, planLevelIds);
  if (loops.length === 0) {
    return undefined;
  }

  const aabb = aabb2ByPolyLine2s(loops);
  if (!aabb || aabb.maxX - aabb.minX < DIST_EPSILON) {
    return undefined;
  }

  return { uMin: aabb.minX, uMax: aabb.maxX };
}

function topologyPlanarGraphRoomBackedPlaneUVFaceLoops(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): PolyLine2[] {
  const planarGraph = topology.planeTopology.planarGraph;
  const faceWindingDirs = planarGraph.faceWindingDirs ?? [];
  const loops: PolyLine2[] = [];

  for (let faceIndex = 0; faceIndex < planarGraph.faceHalfEdges.length; faceIndex++) {
    if (faceWindingDirs[faceIndex] !== WindingDir.CCW) {
      continue;
    }
    if (!faceHasRoomBackedTopologyContributor(topology, buildingGraph, faceIndex, planLevelIds)) {
      continue;
    }
    const loop = planarGraphHalfEdgeStripToPolyLine2(planarGraph, planarGraph.faceHalfEdges[faceIndex]);
    loops.push(loop);
  }

  return loops;
}

function topologyRoomBackedPlaneUVFaceLoops(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): PolyLine2[] {
  const faceNodeLoops = topologyFaceNodeRoomBackedPlaneUVFaceLoops(topology, buildingGraph, planLevelIds);
  if (faceNodeLoops.length > 0) {
    return faceNodeLoops;
  }

  return topologyPlanarGraphRoomBackedPlaneUVFaceLoops(topology, buildingGraph, planLevelIds);
}

export function topologyRoomBackedPlaneUVAABB(
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  planLevelIds?: ReadonlySet<string>,
): AABB2 | undefined {
  const loops = topologyRoomBackedPlaneUVFaceLoops(topology, buildingGraph, planLevelIds);
  if (loops.length === 0) {
    return undefined;
  }

  const unionAabb = aabb2ByPolyLine2s(loops);
  if (!unionAabb) {
    return undefined;
  }

  const searchAabb = { ...unionAabb };
  aabb2Grow(searchAabb, -DIST_EPSILON, -DIST_EPSILON);
  return searchAabb;
}

export interface TopologyPlanUBounds {
  readonly uMin: number;
  readonly uMax: number;
}
